import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { useMemo } from 'react'
import { merchantTheme } from 'theme'

export type MerchantBrand = {
  // The merchant's display name, or null when the merchant record has not
  // resolved yet. Callers pick their own fallback copy (the greeting swaps to a
  // name-less variant rather than rendering an empty possessive).
  name: string | null
  // A CSS color for the merchant-branded controls inside the phone mock.
  color: string
  // The merchant's logo for the phone's app header, or null when they have none.
  // Already sentinel-checked, so callers can treat non-null as "renderable".
  logoUrl: string | null
}

// Fallback brand color for a merchant with no configured primary color. Keeps
// the phone's send button / suggestion chips on the Thanx coral they shipped
// with rather than collapsing to an unstyled control.
export const DEFAULT_BRAND_COLOR = merchantTheme.colors.cayenne50

// A logo-less merchant's `logo_image_urls.default` is NOT null — Paperclip returns
// its `.../missing.png` sentinel — so a truthiness check alone would hand callers a
// URL that renders as a broken image. Same guard as LiveUpdates/index.tsx and
// CampaignCenter/Builder/components/Previews/PushRedesigned.tsx.
const PAPERCLIP_MISSING_URL = /\/missing\.\w+(\?|$)/

// Exported so the sentinel rule is testable on its own, without standing up a
// merchant fixture per case.
export const resolveMerchantLogoUrl = (
  url: string | null | undefined
): string | null => (url && !PAPERCLIP_MISSING_URL.test(url) ? url : null)

/**
 * Merchant branding for the phone simulator (ENG-977 / ENG-979).
 *
 * The Figma phone is merchant-branded throughout: the app header carries the
 * merchant's LOGO, the greeting and sender label name the merchant, and the
 * suggestion pills and send button take the merchant's own brand color (red for the
 * "PizzaCo" mock). The sandbox had no brand plumbing at all, so this
 * hook is the single seam for it — it reads the already-loaded current merchant
 * (`hooks/useCurrentMerchant`, the same source the reward/points previews use for
 * `primary_color`) rather than adding a fetch, and it falls back to the Thanx
 * coral so an un-branded merchant still renders a styled control.
 */
export function useMerchantBrand(): MerchantBrand {
  const merchant = useCurrentMerchant()

  return useMemo(
    () => ({
      name: merchant?.name || null,
      color: merchant?.primary_color || DEFAULT_BRAND_COLOR,
      logoUrl: resolveMerchantLogoUrl(merchant?.logo_image_urls?.default),
    }),
    [merchant]
  )
}

export default useMerchantBrand
